import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle } from 'lucide-react';
import { contactInfo } from '../data/mock';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    address: '',
    message: '',
    preferredContact: 'phone'
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSelectChange = (name, value) => {
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/submit-quote`, formData);
      
      if (response.data.status === 'success') {
        setIsSubmitted(true);
        toast.success('Quote request submitted! We\'ll contact you within 24 hours.');
        
        // Reset form after 3 seconds
        setTimeout(() => {
          setFormData({
            name: '',
            email: '',
            phone: '',
            service: '',
            address: '',
            message: '',
            preferredContact: 'phone'
          });
          setIsSubmitted(false);
        }, 3000);
      }
    } catch (error) {
      // Error already shown to user via toast
      toast.error(
        error.response?.data?.detail || 
        'Failed to submit request. Please call us at (720) 298-9993.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-navy-dark to-navy text-white py-24 mt-16">
        <div className="absolute inset-0 opacity-10">
          <img 
            src="https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=1920&q=80"
            alt="Background"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            Get Your <span className="text-gold">Free Quote</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Schedule a free consultation and receive a transparent, no-obligation estimate 
            for your window and door replacement project
          </p>
        </div>
      </section>

      {/* Contact Form & Info Section */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Information */}
            <div className="lg:col-span-1 space-y-6">
              <Card className="border-2 border-gold/30 hover:border-gold transition-colors">
                <CardHeader>
                  <CardTitle className="text-2xl text-navy-dark">Get In Touch</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-start gap-4 group">
                    <div className="w-12 h-12 bg-gold/10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-gold/20 transition-colors">
                      <Phone className="text-gold" size={24} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-navy-dark mb-1">Phone</h3>
                      <a 
                        href={`tel:${contactInfo.phoneRaw}`}
                        className="text-gray-600 hover:text-gold transition-colors"
                      >
                        {contactInfo.phone}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 group">
                    <div className="w-12 h-12 bg-gold/10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-gold/20 transition-colors">
                      <Mail className="text-gold" size={24} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-navy-dark mb-1">Email</h3>
                      <a 
                        href={`mailto:${contactInfo.email}`}
                        className="text-gray-600 hover:text-gold transition-colors break-all"
                      >
                        {contactInfo.email}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 group">
                    <div className="w-12 h-12 bg-gold/10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-gold/20 transition-colors">
                      <MapPin className="text-gold" size={24} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-navy-dark mb-1">Address</h3>
                      <p className="text-gray-600">
                        {contactInfo.address}<br />
                        {contactInfo.city}, {contactInfo.state} {contactInfo.zip}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 group">
                    <div className="w-12 h-12 bg-gold/10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-gold/20 transition-colors">
                      <Clock className="text-gold" size={24} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-navy-dark mb-1">Hours</h3>
                      <div className="text-gray-600 text-sm space-y-1">
                        <p>{contactInfo.hours.weekday}</p>
                        <p>{contactInfo.hours.saturday}</p>
                        <p>{contactInfo.hours.sunday}</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-navy-dark to-navy text-white border-0">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold mb-3">Emergency Service</h3>
                  <p className="text-gray-300 mb-4">
                    24/7 emergency repairs available for urgent situations
                  </p>
                  <a href={`tel:${contactInfo.phoneRaw}`}>
                    <Button className="w-full bg-gold text-navy-dark hover:bg-gold-light font-semibold">
                      Call Now
                    </Button>
                  </a>
                </CardContent>
              </Card>
            </div>

            {/* Quote Request Form */}
            <div className="lg:col-span-2">
              <Card className="border-2 border-navy-dark/10">
                <CardHeader>
                  <CardTitle className="text-3xl text-navy-dark">Request a Free Estimate</CardTitle>
                  <p className="text-gray-600 mt-2">
                    Fill out the form below and we'll contact you within 24 hours to schedule your free consultation
                  </p>
                </CardHeader>
                <CardContent>
                  {isSubmitted ? (
                    <div className="text-center py-12">
                      <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        <CheckCircle className="text-green-600" size={48} />
                      </div>
                      <h3 className="text-2xl font-bold text-navy-dark mb-3">
                        Thank You!
                      </h3>
                      <p className="text-gray-600 text-lg">
                        Your quote request has been received. We'll be in touch soon!
                      </p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="name" className="text-navy-dark font-semibold">
                            Full Name *
                          </Label>
                          <Input
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            placeholder="John Smith"
                            className="border-gray-300 focus:border-gold focus:ring-gold"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="phone" className="text-navy-dark font-semibold">
                            Phone Number *
                          </Label>
                          <Input
                            id="phone"
                            name="phone"
                            type="tel"
                            value={formData.phone}
                            onChange={handleChange}
                            required
                            placeholder="(719) 555-0123"
                            className="border-gray-300 focus:border-gold focus:ring-gold"
                          />
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="email" className="text-navy-dark font-semibold">
                            Email Address *
                          </Label>
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            placeholder="john@example.com"
                            className="border-gray-300 focus:border-gold focus:ring-gold"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="service" className="text-navy-dark font-semibold">
                            Service Needed *
                          </Label>
                          <Select 
                            value={formData.service}
                            onValueChange={(value) => handleSelectChange('service', value)}
                            required
                          >
                            <SelectTrigger className="border-gray-300 focus:border-gold focus:ring-gold">
                              <SelectValue placeholder="Select a service" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="window-replacement">Window Replacement</SelectItem>
                              <SelectItem value="patio-doors">Patio Door Installation</SelectItem>
                              <SelectItem value="entry-doors">Entry Door Replacement</SelectItem>
                              <SelectItem value="bay-bow">Bay & Bow Windows</SelectItem>
                              <SelectItem value="glass-replacement">Glass Replacement</SelectItem>
                              <SelectItem value="repairs-parts">Repairs and Parts</SelectItem>
                              <SelectItem value="not-sure">Not Sure / Multiple</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="address" className="text-navy-dark font-semibold">
                          Property Address
                        </Label>
                        <Input
                          id="address"
                          name="address"
                          value={formData.address}
                          onChange={handleChange}
                          placeholder="123 Main St, Colorado Springs, CO"
                          className="border-gray-300 focus:border-gold focus:ring-gold"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="preferredContact" className="text-navy-dark font-semibold">
                          Preferred Contact Method
                        </Label>
                        <Select 
                          value={formData.preferredContact}
                          onValueChange={(value) => handleSelectChange('preferredContact', value)}
                        >
                          <SelectTrigger className="border-gray-300 focus:border-gold focus:ring-gold">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="phone">Phone Call</SelectItem>
                            <SelectItem value="email">Email</SelectItem>
                            <SelectItem value="text">Text Message</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="message" className="text-navy-dark font-semibold">
                          Project Details
                        </Label>
                        <Textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          rows={5}
                          placeholder="Tell us about your project... How many windows/doors? Any specific concerns or preferences?"
                          className="border-gray-300 focus:border-gold focus:ring-gold resize-none"
                        />
                      </div>

                      <Button 
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full bg-gold text-navy-dark hover:bg-gold-light text-lg py-6 font-bold shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSubmitting ? 'Submitting...' : 'Submit Request'}
                        <Send className="ml-2" size={20} />
                      </Button>

                      <p className="text-sm text-gray-500 text-center">
                        By submitting this form, you agree to be contacted about your project. 
                        We respect your privacy and never share your information.
                      </p>
                    </form>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
